import { postArcgisQuery } from "../arcgisQuery";
import { esriFeatureToGeoJson } from "./esriGeometry";

// ArcGIS REST feature extraction for zone land data.
//
// Queries <layer.url>/query via POST (form-encoded) — real farm polygons
// exceed GET URL limits. `inSR=4326` and `f=geojson` were verified live on
// both hosts (druid.hutton.ac.uk, maps.gov.scot) on 07/07/2026, so the server
// returns WGS84 GeoJSON directly and no esriJSON conversion is needed.
//
// EXCEPT on layers that set `geometryFormat: "esriJson"`. Those services convert
// their own geometry badly, dropping interior rings, so we take esriJSON and
// convert it ourselves. See `esriGeometry.js` for the measurements. The default
// stays `f=geojson`: only a layer proven to need it should opt in, because
// converting geometry we do not have to convert is added risk for no gain.

/**
 * Fetch features from an ArcGIS REST layer intersecting a zone polygon.
 * @param {object} layer entry from ZONE_LAND_LAYERS (protocol 'arcgis')
 * @param {object} zonePolygon GeoJSON Polygon geometry (WGS84, [lng, lat])
 * @returns {Promise<{features: Array, truncated: boolean}>} GeoJSON Features
 *   (WGS84) and whether the server hit its maxRecordCount and had more to give.
 */
export async function fetchArcgisFeatures(layer, zonePolygon) {
  const geometry = JSON.stringify({
    rings: zonePolygon.coordinates,
    spatialReference: { wkid: 4326 },
  });

  const wantsEsriJson = layer.geometryFormat === "esriJson";

  const params = new URLSearchParams({
    f: wantsEsriJson ? "json" : "geojson",
    geometry,
    geometryType: "esriGeometryPolygon",
    inSR: "4326",
    outSR: "4326",
    spatialRel: "esriSpatialRelIntersects",
    outFields: "*",
    returnGeometry: "true",
  });

  // Transport, error and truncation handling live in one place — shared with the
  // map vector outline adapter, which queries the same servers by envelope.
  const { features, truncated } = await postArcgisQuery(layer.url, params, layer.id);

  if (!wantsEsriJson) return { features, truncated };

  // A feature that yields no usable geometry is dropped rather than failing the
  // whole layer, matching how the intersect maths already skips a feature that
  // will not clip.
  const converted = [];
  for (const feature of features || []) {
    const geoJsonFeature = esriFeatureToGeoJson(feature);
    if (geoJsonFeature) converted.push(geoJsonFeature);
  }

  return { features: converted, truncated };
}
